import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import * as React from "react";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useNavigate } from "react-router-dom";
import ClientContext from "../../layouts/ClientContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useQueryGetParentRenewalPage,
  fetchAllParentRenewalRows,
} from "../../data/services/clientService";
import { saveParentRenewalWorkbook } from "./parentRenewalExport";
import UserContext from "../../layouts/UserContext";
import OEContext from "../../layouts/OEContext";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import useDebounce from "../../hooks/useDebounce";
import { getFilterValues, StatusColumnMenu } from "./parentDashboardStatusFilter";
import "@progress/kendo-theme-default/dist/all.css";
import "./ParentRenewalDashboard.css";

// Shared by Status, Group Status and the child grid's Status, so a badge sits
// under the middle of its header in every one of them.
const BADGE_CELL_STYLE = { textAlign: "center" };

// Export to Excel picks this color up while it is clickable, so an available
// action reads differently from an unavailable one -- Kendo's disabled styling
// already mutes the button, so this is only ever applied alongside
// `disabled={false}`. Clear Filters instead reuses the checkbox menu's own
// `themeColor="primary"` (see the Button below), rather than a second
// hard-coded color here.
export const EXCEL_GREEN = "#217346"; // Microsoft Excel's own icon green.

const EXCEL_BUTTON_ACTIVE_STYLE = {
  backgroundColor: EXCEL_GREEN,
  borderColor: EXCEL_GREEN,
  color: "#fff",
};

const STATUS_COLOR_MAP = {
  InProgress: "orange",
  Submitted: "green",
  Published: "green",
  PublishError: "red",
  PublishInProgress: "yellow",
  // Distinct from the grey NotStarted falls back to -- "not in season" means there is
  // no OE work to start, which reads very differently from "hasn't started yet."
  NotInSeason: "#6c5ce7",
};

// Lets a disabled Kendo Button still show a native tooltip on hover -- a
// disabled element swallows its own mouse events, so the title has to live
// on a wrapping span instead. Same pattern as ClientMenuFieldOverrideEditor.
const DISABLED_ACTION_TOOLTIP_STYLE = { display: "inline-block" };

const NOT_IN_SEASON_TOOLTIP =
  "This company is not enrolled in the current Open Enrollment period, so there are no OE details to view.";

const StatusBadge = ({ text, statusKey }) => {
  const bg = STATUS_COLOR_MAP[statusKey] || "grey";
  return (
    <span
      style={{
        padding: "2px 8px",
        backgroundColor: bg,
        borderRadius: "15px",
        color: bg === "yellow" ? "black" : "white",
        fontSize: "12px",
      }}
    >
      {text}
    </span>
  );
};

const ParentRenewalDashboard = () => {
  const navigate = useNavigate();
  const { setClient } = React.useContext(ClientContext);
  const { loggedInUser } = React.useContext(UserContext);
  const oePeriod = React.useContext(OEContext);
  const isAdminUser = !!loggedInUser?.hasAdminAppPrivilege;
  const isBenefitAdminUser = !!loggedInUser?.benefitAdminPrivilege;

  const [gridExpand, setGridExpand] = React.useState({});
  const [showAllClients, setShowAllClients] = React.useState("Yes");
  const [allClientOption, setAllClientOption] = React.useState("All Clients");
  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [gridDataState, setGridDataState] = React.useState({
    take: 20,
    skip: 0,
    sort: [],
    filter: { logic: "and", filters: [] },
  });

  const page = Math.floor(gridDataState.skip / gridDataState.take) + 1;
  // Both columns filter with checkboxes, so several statuses can be ticked at
  // once. They travel to the server as one comma-separated parameter; no status
  // description contains a comma.
  const statusFilter = getFilterValues(gridDataState.filter, "oeStatusDesc").join(",");
  const groupStatusFilter = getFilterValues(
    gridDataState.filter,
    "rollupStatusDesc",
  ).join(",");

  const { data: apiResponse, isFetching } = useQueryGetParentRenewalPage(
    oePeriod?.oePeriodId,
    showAllClients,
    page,
    gridDataState.take,
    gridDataState.sort,
    debouncedSearchTerm,
    statusFilter,
    groupStatusFilter,
  );

  const payload = apiResponse?.data;
  const pagedData = payload?.page;
  const mainRows = pagedData?.items || [];
  const groupingAvailable = payload?.groupingAvailable ?? true;
  const relatedCodeExceptions = payload?.exceptions || [];
  const [showExceptions, setShowExceptions] = React.useState(false);

  const [isExporting, setIsExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState(null);

  // The grid is server-paged, so exporting what is on screen would silently produce
  // 20 rows out of a thousand. This pulls every page, then flattens each group into
  // its parent row followed by its related companies -- the nesting the grid shows
  // has no equivalent in a spreadsheet, so the relationship is carried in columns
  // (Row Type / Parent Company ID) instead.
  const onExportToExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const groups = await fetchAllParentRenewalRows(
        oePeriod?.oePeriodId,
        showAllClients,
        debouncedSearchTerm,
        statusFilter,
        groupStatusFilter,
        gridDataState.sort,
      );

      // The period name reaches a file name, so strip anything the OS rejects in
      // one -- otherwise a period named "OE 2025/26" produces a failed save with
      // no explanation.
      const safePeriod = (oePeriod?.oePeriodName || "export").replace(
        /[^A-Za-z0-9._-]+/g,
        "-",
      );

      await saveParentRenewalWorkbook(
        groups,
        `parent-renewal-dashboard-${safePeriod}.xlsx`,
      );
    } catch {
      // A full-season export is a dozen-plus requests; any one of them failing
      // used to reject silently, leaving the button to flick back to "Export to
      // Excel" as though it had worked and no file to show for it.
      setExportError(
        "The export could not be completed. Please try again; if it keeps failing, contact support.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Business asked for the Prism-correction banner to be hidden for now. The API
  // still returns `exceptions` and the banner below still works -- flip this to true
  // to bring it back rather than rewriting it.
  const SHOW_RELATED_CODE_EXCEPTIONS = false;

  React.useEffect(() => {
    if (loggedInUser && !loggedInUser.isWhiteListed) navigate("/under-maintenance");
  }, [loggedInUser, navigate]);

  const onClientFilterChange = (e) => {
    setShowAllClients(e.value === "All Clients" ? "Yes" : "No");
    setAllClientOption(e.value);
    setGridDataState({ ...gridDataState, skip: 0 });
  };

  const actionCell = (props) => {
    if (props.rowType === "groupHeader") {
      return null;
    }

    const isParentCompany = props.dataItem.clientId === props.dataItem.parentClientId;
    const isDisabled = !!props.dataItem.notInSeason;

    // nowrap keeps View Setup and View Rates side by side. Without it they wrap
    // into a stack as soon as the column is a few pixels too narrow for both,
    // which doubles the height of every row on the page.
    return (
      <td style={{ whiteSpace: "nowrap" }}>
        <span
          style={isDisabled ? DISABLED_ACTION_TOOLTIP_STYLE : undefined}
          title={isDisabled ? NOT_IN_SEASON_TOOLTIP : undefined}
        >
          {isAdminUser ? (
            <>
              <Button
                disabled={isDisabled}
                onClick={() => {
                  setClient(props.dataItem);
                  navigate(`/admin/clients/${props.dataItem.clientId}/setup`);
                }}
              >
                View Setup
              </Button>
              <Button
                themeColor="info"
                style={{ marginLeft: "10px" }}
                disabled={isDisabled}
                onClick={() => {
                  setClient({ ...props.dataItem, parent: isParentCompany });
                  navigate(
                    `/admin/clients/${props.dataItem.clientId}/oe/${props.dataItem.oePeriodId}/summary`,
                  );
                }}
              >
                View Rates
              </Button>
            </>
          ) : (
            <Button
              themeColor="info"
              disabled={isDisabled}
              onClick={() => {
                setClient(props.dataItem);
                navigate(
                  `/clients/${props.dataItem.clientId}/oe/${props.dataItem.oePeriodId}/summary`,
                );
              }}
            >
              Review
            </Button>
          )}
        </span>
      </td>
    );
  };

  // Kendo renders nothing for a <Column> with neither field nor cell, and the
  // column collapses to zero width. The child grid's alignment spacers therefore
  // need a real (empty) cell renderer or they silently do not exist.
  const spacerCell = () => <td />;

  const groupStatusCell = (props) => {
    if (props.rowType === "groupHeader") {
      return null;
    }

    const { hasSubClients, rollupStatus, rollupStatusDesc } = props.dataItem;

    // Only a parent with related companies has a group. A standalone client leaves
    // this blank rather than echoing its own badge back in a second column.
    if (!hasSubClients || !rollupStatusDesc) {
      return <td />;
    }

    return (
      <td style={BADGE_CELL_STYLE}>
        <StatusBadge text={rollupStatusDesc} statusKey={rollupStatus} />
      </td>
    );
  };

  const statusCell = (props) => {
    if (props.rowType === "groupHeader") {
      return null;
    }

    const { oeStatus, oeStatusDesc } = props.dataItem;

    // The company's own status, parent or not. This used to show the group rollup
    // for any row with children, which hid the parent's real status behind an
    // aggregate: a published parent with one not-started subsidiary displayed
    // "In Progress" while the Published count beside it read 1. The four count
    // columns already carry the group's spread; this column is the company itself.
    //
    // A synthesized parent row -- a company that exists but is not enrolled this
    // period -- has no status and falls through to the empty cell below.
    if (!oeStatusDesc) {
      return <td />;
    }

    return (
      <td style={BADGE_CELL_STYLE}>
        <StatusBadge text={oeStatusDesc} statusKey={oeStatus} />
      </td>
    );
  };

  const childStatusCell = (props) => {
    const { oeStatus, oeStatusDesc } = props.dataItem;
    if (!oeStatusDesc) {
      return <td />;
    }
    return (
      <td style={BADGE_CELL_STYLE}>
        <StatusBadge text={oeStatusDesc} statusKey={oeStatus} />
      </td>
    );
  };

  const rollupCountCell = (field) => (props) => {
    if (props.rowType === "groupHeader") {
      return null;
    }
    return <td>{props.dataItem[field] ?? 0}</td>;
  };

  const DetailComponent = (props) => {
    const subClients = props.dataItem.subClients;

    if (!subClients || subClients.length === 0) {
      return null;
    }

    return (
      // Same tint and left rule as the expanded parent row above, so the block reads
      // as one unit. The caption states the relationship in words rather than asking
      // anyone to infer it from indentation.
      <div
        style={{
          backgroundColor: "#eef4fb",
          borderLeft: "4px solid #2b6cb0",
          padding: "6px 0 10px 0",
        }}
      >
        <div
          style={{
            padding: "4px 12px 8px 12px",
            fontSize: "12px",
            fontWeight: 600,
            color: "#2b6cb0",
          }}
        >
          {subClients.length === 1
            ? "1 related company under "
            : `${subClients.length} related companies under `}
          {props.dataItem.clientName}
        </div>
        <Grid data={subClients} scrollable="none">
          {/*
            Column geometry mirrors the parent grid so Company ID, Company Name and
            Status line up under their headers instead of drifting left.

            Company Name has no width here either, for the same reason as the
            parent grid: it needs to be the one column Kendo can flex to fill the
            container, and its floor comes from the same
            parent-renewal-company-name CSS class instead of a width prop. Because
            every other column below sums to the same 1060px as the parent
            grid's (the 470px spacer stands in for Group Status + four rollup
            counts), Company Name resolves to the same width in both grids and
            the columns still line up.
              50  expander / spacer
              110 Company ID
              --- Company Name (flex, 200px floor) ---
              190 Status
              470 spacer = Group Status 190 + four 70px rollup counts
              240 Action
            Change a width in the parent grid and the matching number here must move.
          */}
          <Column title="" cell={spacerCell} width="50px" sortable={false} />
          <Column field="clientId" title="Company ID" width="110px" />
          <Column
            field="clientName"
            title="Company Name"
            className="parent-renewal-company-name"
            headerClassName="parent-renewal-company-name"
          />
          <Column field="oeStatusDesc" title="Status" cell={childStatusCell} width="190px" />
          <Column title="" cell={spacerCell} width="470px" sortable={false} />
          <Column title="Action" cell={actionCell} width="240px" sortable={false} />
        </Grid>
      </div>
    );
  };

  const expandCell = (props) => {
    if (props.rowType === "groupHeader") {
      return null;
    }

    const hasSubClients = (props.dataItem.subClients || []).length > 0;

    return (
      <td>
        {hasSubClients ? (
          <Button
            themeColor="secondary"
            size="small"
            fillMode="flat"
            rounded="full"
            aria-label={
              gridExpand[props.dataItem.clientId]
                ? "Hide child companies"
                : "Show child companies"
            }
            style={{ minWidth: "32px", width: "32px", padding: "4px" }}
            onClick={() => {
              setGridExpand((currentState) => ({
                ...currentState,
                [props.dataItem.clientId]: !currentState[props.dataItem.clientId],
              }));
            }}
          >
            <FontAwesomeIcon
              icon={gridExpand[props.dataItem.clientId] ? faChevronUp : faChevronDown}
            />
          </Button>
        ) : null}
      </td>
    );
  };

  const customCellRender = (td, props) => {
    if (props?.rowType === "data" && td?.props?.className?.includes("k-hierarchy-cell")) {
      return <td className="k-hierarchy-cell" />;
    }
    return td;
  };

  return (
    <div>
      {!groupingAvailable && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: "12px",
            border: "1px solid #e0b000",
            backgroundColor: "#fff8e1",
            borderRadius: "4px",
          }}
        >
          Related-company grouping is unavailable right now. All clients are shown
          individually; this does not mean they have no related companies.
        </div>
      )}

      {exportError && (
        <div
          role="alert"
          style={{
            padding: "10px 14px",
            marginBottom: "12px",
            border: "1px solid #c53030",
            backgroundColor: "#fff5f5",
            borderRadius: "4px",
            color: "#c53030",
          }}
        >
          {exportError}
        </div>
      )}

      {SHOW_RELATED_CODE_EXCEPTIONS && relatedCodeExceptions.length > 0 && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: "12px",
            border: "1px solid #d0d0d0",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
          }}
        >
          <span>
            {relatedCodeExceptions.length}{" "}
            {relatedCodeExceptions.length === 1 ? "client needs" : "clients need"}{" "}
            Prism correction
          </span>
          <Button
            fillMode="flat"
            style={{ marginLeft: "10px" }}
            onClick={() => setShowExceptions((v) => !v)}
          >
            {showExceptions ? "hide" : "show"}
          </Button>
          {showExceptions && (
            <table style={{ marginTop: "10px", fontSize: "12px", width: "100%" }}>
              <thead>
                <tr style={{ textAlign: "left" }}>
                  <th style={{ padding: "4px 8px" }}>Client</th>
                  <th style={{ padding: "4px 8px" }}>Name</th>
                  <th style={{ padding: "4px 8px" }}>Problem</th>
                  <th style={{ padding: "4px 8px" }}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {relatedCodeExceptions.map((e) => (
                  <tr key={e.clientId}>
                    <td style={{ padding: "4px 8px" }}>{e.clientId}</td>
                    <td style={{ padding: "4px 8px" }}>{e.clientName}</td>
                    <td style={{ padding: "4px 8px" }}>{e.exceptionType}</td>
                    <td style={{ padding: "4px 8px" }}>{e.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "20px 0",
        }}
      >
        <div style={{ display: "flex", width: "50%" }}>
          <Input
            placeholder="Search Company Id or Name..."
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setGridDataState({ ...gridDataState, skip: 0 });
            }}
            value={searchTerm}
          />
          {isBenefitAdminUser && (
            <DropDownList
              style={{ width: "150px", marginLeft: "10px" }}
              data={["All Clients", "My Clients"]}
              value={allClientOption}
              onChange={onClientFilterChange}
            />
          )}
          <Button
            themeColor="base"
            style={{
              marginLeft: "10px",
              whiteSpace: "nowrap",
              ...(isExporting ? {} : EXCEL_BUTTON_ACTIVE_STYLE),
            }}
            onClick={onExportToExcel}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Export to Excel"}
          </Button>
        </div>
        <Button
          // Matches the checkbox menu's own "Filter" button (StatusColumnMenu),
          // so Clear Filters reads as the undo of that action rather than an
          // unrelated color.
          themeColor={statusFilter || groupStatusFilter ? "primary" : "base"}
          style={{ whiteSpace: "nowrap" }}
          disabled={!statusFilter && !groupStatusFilter}
          onClick={() =>
            setGridDataState({
              ...gridDataState,
              skip: 0,
              filter: { logic: "and", filters: [] },
            })
          }
        >
          Clear Filters
        </Button>
      </div>
      <Grid
        style={{ height: "700px" }}
        total={pagedData?.totalCount || 0}
        {...gridDataState}
        loading={isFetching}
        onDataStateChange={(e) => setGridDataState(e.dataState)}
        pageable={{ buttonCount: 5, pageSizes: [10, 20, 50] }}
        sortable={true}
        // Deliberately no `filterable`: that draws Kendo's filter row beneath the
        // header, and the Status / Group Status filters live in the header menu.
        filter={gridDataState.filter}
        cellRender={customCellRender}
        data={mainRows.map((item) => ({
          ...item,
          expanded: !!gridExpand[item.clientId],
        }))}
        expandField="expanded"
        onExpandChange={(e) => {
          setGridExpand({
            ...gridExpand,
            [e.dataItem.clientId]: e.value,
          });
        }}
        detail={DetailComponent}
        rowRender={(trElement, rowProps) => {
          // Tint an expanded parent and rule a line down its left edge, so the
          // children below it read as belonging to that row rather than floating
          // between two unrelated companies.
          // Group header and footer rows carry an aggregate, not a client, so
          // dataItem can be absent -- reading clientId off it directly would throw.
          if (!rowProps.dataItem?.clientId || !gridExpand[rowProps.dataItem.clientId]) {
            return trElement;
          }

          return React.cloneElement(
            trElement,
            {
              style: {
                ...trElement.props.style,
                backgroundColor: "#eef4fb",
                borderLeft: "4px solid #2b6cb0",
                fontWeight: 600,
              },
            },
            trElement.props.children,
          );
        }}
      >
        {/*
          Every column but Company Name is fixed-width; their sum (1060px) is
          the floor the grid can never shrink below, which fits a laptop screen
          once the nav rail and page padding are accounted for. The four count
          columns carry a number, so they are sized for their headers (which
          wrap to two lines) rather than their contents. Status and Group
          Status are 190px because "Submitted for Review" -- the longest
          status badge -- does not fit at 150px; it was clipped there.

          Company Name is deliberately the one column left without a width:
          Kendo only pins the whole table to one fixed pixel total when every
          column has a width, so leaving this one unset keeps the grid filling
          100% of its container and growing on a wide monitor -- exactly what a
          fixed width here took away. Its own floor comes from the
          parent-renewal-company-name CSS class (ParentRenewalDashboard.css)
          instead, applied to both the header and the cell so they stay aligned
          if the fixed columns ever squeeze it down to that floor.

          Any change here must be mirrored in DetailComponent's spacer above.
        */}
        <Column title="" cell={expandCell} sortable={false} width="50px" />
        <Column field="clientId" title="Company ID" width="110px" />
        <Column
          field="clientName"
          title="Company Name"
          className="parent-renewal-company-name"
          headerClassName="parent-renewal-company-name"
        />
        <Column
          field="oeStatusDesc"
          title="Status"
          cell={statusCell}
          sortable={true}
          columnMenu={StatusColumnMenu}
          width="190px"
        />
        <Column
          field="rollupStatusDesc"
          title="Group Status"
          cell={groupStatusCell}
          sortable={true}
          columnMenu={StatusColumnMenu}
          width="190px"
        />
        <Column
          title="Not Started"
          cell={rollupCountCell("rollupNotStartedCount")}
          width="70px"
          sortable={false}
        />
        <Column
          title="In Progress"
          cell={rollupCountCell("rollupInProgressCount")}
          width="70px"
          sortable={false}
        />
        <Column
          title="Submitted"
          cell={rollupCountCell("rollupSubmittedCount")}
          width="70px"
          sortable={false}
        />
        <Column
          title="Published"
          cell={rollupCountCell("rollupPublishedCount")}
          width="70px"
          sortable={false}
        />
        <Column title="Action" cell={actionCell} width="240px" sortable={false} />
      </Grid>
    </div>
  );
};

export default ParentRenewalDashboard;
